import { isProd, getTestingEnv } from 'utils/environment'
import {
    APP_TYPE_SUGARCUBE,
    APP_TYPE_MAGICWINDOW,
    APP_ENV_TESTING,
    APP_ENV_PROD,
    APP_TYPE_DESIGN_HOME,
    APP_TYPE_KIOSK,
} from 'models/tracking/constants'
import { getPathRegion, getLocaleFromRegion } from 'models/location/selectors'

let initMixpanelTime
let loadingStartTime = null
let heartbeatIntervalId = null
let heartbeatMinute = 0

let designFeedbackTriggerTimeoutId1 = null
let designFeedbackTriggerTimeoutId2 = null

const trackingEnabled = () => !!(window.mixpanel || window.pendo)

const pendoQueue = []
const queuePendo = (eventName, options) => {
    if (eventName) {
        pendoQueue.push([eventName, options])
    }

    if (!window.pendo.track) {
        setTimeout(queuePendo, 500)
    } else {
        pendoQueue.forEach((item) => window.pendo.track(item[0], item[1]))
    }
}

export const initMixpanel = () => {
    initMixpanelTime = Date.now()
    if (trackingEnabled()) {
        if (isProd) {
            window.mixpanel.init('29a3c10bc553189c0e5b6c5dcded291a')
        } else {
            window.mixpanel.init('a7a560b7ce99351f21ced5f60aa7d965')
        }
    }
}

export const markLoadingStart = () => {
    loadingStartTime = Date.now()
}

export const alias = (id) =>
    new Promise((resolve) => {
        // export const alias = id => track('$create_alias', { alias: id })
        // New user
        if (trackingEnabled()) {
            window.mixpanel.alias(id)
        }
        resolve()
    })

export const identify = (id) =>
    new Promise((resolve) => {
        // Logged in user
        if (trackingEnabled()) {
            window.mixpanel.identify(id)
        }
        resolve()
    })

export const initPendo = (id, options) =>
    new Promise((resolve) => {
        if (window.pendo) {
            const locale = getLocaleFromRegion(getPathRegion())
            if (id) {
                window.pendo.initialize({
                    visitor: { id, email: options.email, locale },
                    account: { id },
                })
                // Make the email available globaly
                // for use in the pendo feedback form
                window.pendoGlobalEmail = options.email
            } else {
                window.pendo.initialize({
                    id: 'VISITOR-UNIQUE-ID',
                    visitor: { locale },
                })
            }
            if (window.pendo.getVisitorId) {
                window.pendoGlobalVisitorId = window.pendo.getVisitorId()
            }
        }
        resolve()
    })

export const initAppGlobals = ({ isMagicWindow = false, isDesignHome = false, isKiosk = false }) => {
    const isTestingEnvironment = getTestingEnv()
    window.appType = isMagicWindow ? APP_TYPE_MAGICWINDOW : APP_TYPE_SUGARCUBE

    // So overwrite the appType if you are in the DesignHome experience since it is also
    // a magic window ie. has magicWindow in the iframe url
    if (isDesignHome) {
        window.appType = APP_TYPE_DESIGN_HOME
    }
    if (isKiosk) {
        window.appType = APP_TYPE_KIOSK
    }

    window.appEnvironment = isTestingEnvironment ? APP_ENV_TESTING : APP_ENV_PROD
}

export const track = (eventName, options) =>
    new Promise((resolve) => {
        const completeOptions = {
            globalRendererSessionId: window.globalRendererSessionId,
            Application: 'Sugarcube.com',
            AppType: window.appType ? window.appType : APP_TYPE_SUGARCUBE,
            AppEnvironment: window.appEnvironment ? window.appEnvironment : APP_ENV_PROD,
            ...options,
        }

        if (window.pendo && window.pendo.getVisitorId) {
            completeOptions.PendoVisitorId = window.pendo.getVisitorId()
        }

        if (!isProd) {
            /* eslint-disable no-console */
            console.group('Tracking')
            console.log('Event name:      ', eventName)
            console.log('Metadata:        ', completeOptions)
            console.log('Tracking enabled:', trackingEnabled())
            console.groupEnd()
        }

        if (trackingEnabled()) {
            const promises = []

            if (window.pendo) {
                if (window.pendo.track) {
                    promises.push(
                        new Promise((res) => {
                            window.pendo.track(eventName, completeOptions)
                            res()
                        }),
                    )
                } else {
                    queuePendo(eventName, completeOptions)
                }
            }

            if (window.mixpanel) {
                promises.push(
                    new Promise((res) => {
                        const queued = window.mixpanel.track(eventName, completeOptions, res)
                        if (!queued) {
                            res()
                        }
                    }),
                )
            }

            Promise.all(promises).then(() => resolve())
        } else {
            resolve()
        }
    })

export const set = (options) =>
    new Promise((resolve) => {
        if (!isProd) {
            /* eslint-disable no-console */
            console.group('People API set')
            console.log('Metadata:        ', options)
            console.log('Tracking enabled:', trackingEnabled())
            console.groupEnd()
        }
        if (trackingEnabled()) {
            window.mixpanel.people.set(options, resolve)
        }
    })

export const setOnce = (options) =>
    new Promise((resolve) => {
        if (!isProd) {
            /* eslint-disable no-console */
            console.group('People API set_once')
            console.log('Metadata:        ', options)
            console.log('Tracking enabled:', trackingEnabled())
            console.groupEnd()
        }
        if (trackingEnabled()) {
            window.mixpanel.people.set_once(options, resolve)
        }
    })

export const increment = (propertyName, amnt = 1) =>
    new Promise((resolve) => {
        if (!isProd) {
            /* eslint-disable no-console */
            console.group('People API increment')
            console.log('Property:        ', propertyName)
            console.log('Amount:          ', amnt)
            console.log('Tracking enabled:', trackingEnabled())
            console.groupEnd()
        }
        if (trackingEnabled()) {
            window.mixpanel.people.increment(propertyName, amnt, resolve)
        }
    })

export const reset = () =>
    new Promise((resolve) => {
        if (trackingEnabled()) {
            window.mixpanel.reset()
        }
        resolve()
    })

export const loadGuides = () =>
    new Promise((resolve) => {
        if (trackingEnabled() && window.pendo && window.pendo.loadGuides) {
            window.pendo.loadGuides()
        }
        resolve()
    })

export const visit = (eventName, options) => {
    loadGuides()
    return track(eventName, { ...options, ActionType: 'Visit' })
}

export const showMagicWindowCloseFeedbackForm = () => {
    if (window.pendo && window.pendo.showGuideById) {
        return window.pendo.showGuideById('Bt01WnwZGCtR7ovzhGuWZpfc0DQ')
    }
    return false
}

export const click = (eventName, options) => track(eventName, { ...options, ActionType: 'Click' })
export const hover = (eventName, options) => track(eventName, { ...options, ActionType: 'Hover' })
export const drop = (eventName, options) => track(eventName, { ...options, ActionType: 'Drop' })
export const error = (eventName, options) => track(eventName, { ...options, ActionType: 'Error' })
export const workflow = (eventName, options) => track(eventName, { ...options, ActionType: 'Workflow' })

export const getTimeSinceMaxpanelInit = () => Date.now() - initMixpanelTime
export const getTimeSinceLoadingStart = () => Date.now() - loadingStartTime

const tickHeartbeat = (roomInfo) => {
    workflow('DecorateRoom.SessionMinuteIncrement', {
        ...roomInfo,
        MinuteIncrement: heartbeatMinute,
    })
    loadGuides()
    heartbeatMinute += 1
}

export const initHeartbeat = (roomInfo) => {
    heartbeatMinute = 0
    tickHeartbeat(roomInfo)
    heartbeatIntervalId = window.setInterval(tickHeartbeat, 60000, roomInfo)
}

export const cancelHeartbeat = () => window.clearInterval(heartbeatIntervalId)

export const initDesignFeedbackTriggerEvents = (roomInfo) => {
    if (window.appType !== APP_TYPE_MAGICWINDOW) {
        designFeedbackTriggerTimeoutId1 = window.setTimeout(() => {
            workflow('DecorateRoom.SugarcubeDesignFeedback1', roomInfo).then(() => loadGuides())
        }, 3 * 60 * 1000)
        designFeedbackTriggerTimeoutId2 = window.setTimeout(() => {
            workflow('DecorateRoom.SugarcubeDesignFeedback2', roomInfo).then(() => loadGuides())
        }, 6 * 60 * 1000)
    }
}

export const cancelDesignFeedbackTriggerEvents = () => {
    window.clearTimeout(designFeedbackTriggerTimeoutId1)
    window.clearTimeout(designFeedbackTriggerTimeoutId2)
}

export const optimizelyTrack = (eventName) => {
    if (!isProd) {
        /* eslint-disable no-console */
        console.log('Tracking optimizely event name: ', eventName)
    } else {
        window.optimizely = window.optimizely || []
        window.optimizely.push({
            type: 'event',
            eventName,
            tags: {
                revenue: 0, // Optional in cents as integer (500 == $5.00)
                value: 0.0, // Optional as float
            },
        })
    }
}

export const getIsMagicWindow = () => window.appType === APP_TYPE_MAGICWINDOW
